import React, { useState, useEffect, useContext } from 'react';
import { Container, Grid, TextField, InputAdornment } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { ProposalStatus, DealStatus } from '../../../utils/enums'
import dayjs from 'dayjs';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ContactsAutoComplete from '../../Contacts/ContactsAutoComplete';
import { AuthContext } from '../../../context/AuthContext';

const EditForm = ({ proposalData, onSubmit, loading }) => {
    const { userInfo } = useContext(AuthContext);
    const [topic, setTopic] = useState('');
    const [duration, setDuration] = useState('');
    const [language, setLanguage] = useState('');
    const [location, setLocation] = useState('');
    const [deadline, setDeadline] = useState(null);
    const [proposalStatus, setProposalStatus] = useState(0);
    const [date, setDate] = useState(null);
    const [dealStatus, setDealStatus] = useState(0);
    const [amount, setAmount] = useState(null);
    const [leadId, setLeadId] = useState(null);
    const [shouldFetchSingleLead, setShouldFetchSingleLead] = useState(false);

    useEffect(() => {
        if (proposalData?.id > 0) {
            setTopic(proposalData.topic || '');
            setDuration(proposalData.duration || '');
            setLanguage(proposalData.language || '');
            setLocation(proposalData.location || '');
            setDeadline(proposalData.deadline ? dayjs(proposalData.deadline) : null);
            setProposalStatus(proposalData.proposalStatus || 0);
            setDate(proposalData.date ? dayjs(proposalData.date) : null);
            setDealStatus(proposalData.dealStatus || 0);
            setAmount(proposalData.amount || null);
            setLeadId(proposalData.leadId || null);
            setShouldFetchSingleLead(true);
        }
    }, [proposalData]);

    const handleSubmit = (e) => {
        e.preventDefault();
        // Collect all form data
        const formData = {
            topic,
            duration,
            language,
            location,
            deadline: deadline ? deadline.toISOString() : null,
            proposalStatus,
            date: date ? date.toISOString() : null,
            dealStatus,
            amount,
            leadId
        };
        // Call onSubmit function passed as a prop
        onSubmit(formData);
    };

    return (
        <form id='create-form' onSubmit={handleSubmit}>
            <Container maxWidth="md" sx={{ marginTop: "10px" }}>
                <h4>Proposal Information</h4>
                <Grid container spacing={3}>
                    {/* Topic */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Topic" required
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)} />
                    </Grid>

                    {/* Amount */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Amount"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }} />
                    </Grid>

                    {/* Duration */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Duration"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)} />
                    </Grid>

                    {/* Language */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Language"
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)} />
                    </Grid>

                    {/* Location */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Location"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)} />
                    </Grid>

                    {/* Deadline */}
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Deadline"
                                required
                                fullWidth
                                value={deadline}
                                onChange={(newDate) => setDeadline(newDate)}
                                slotProps={{
                                    textField: {
                                        required: 'true',
                                        variant: 'outlined',
                                        fullWidth: true
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Proposal Status */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disablePortal
                            options={ProposalStatus}
                            sx={{ width: "100%" }}
                            value={ProposalStatus.find((option) => option.value === proposalStatus)}
                            onChange={(event, newValue) => setProposalStatus(newValue ? newValue.value : 0)}
                            renderInput={(params) => <TextField {...params} label="Proposal Status" />}
                        />
                    </Grid>

                    {/* Date */}
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Date"
                                fullWidth
                                value={date}
                                onChange={(newDate) => setDate(newDate)}
                                slotProps={{
                                    textField: {
                                        variant: 'outlined',
                                        fullWidth: true
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Deal Status - Hidden for IdUser role */}
                    {!userInfo?.roles?.includes('IdUser') && (
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                disablePortal
                                options={DealStatus}
                                sx={{ width: "100%" }}
                                value={DealStatus.find((option) => option.value === dealStatus)}
                                onChange={(event, newValue) => setDealStatus(newValue ? newValue.value : 0)}
                                renderInput={(params) => <TextField {...params} label="Deal Status" />}
                            />
                        </Grid>
                    )}

                    {/* Contact */}
                    <Grid item xs={12} sm={6}>
                        <ContactsAutoComplete contactId={leadId} setContactId={setLeadId} shouldFetchSingleContact={shouldFetchSingleLead} />
                    </Grid>

                </Grid>
            </Container >
        </form>
    );
};

export default EditForm;

